import { Dialog, TextField } from '@material-ui/core'
import { Button } from 'components/facets/Button'
import { useUser } from 'components/providers/UserProvider'
import _ from 'lodash'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { InventoryArticle, Location } from 'shared'
import { ESCAPE_STRING_FOR_EMPTY_COMMENT, LOCATIONS_PANEL } from 'utils/constants'
import { VisualArticle } from '../stockPricing/StockDisplay/VisualArticle'

export interface OnboardingEditLocationsDialogProps {
  open: boolean
  article: InventoryArticle
  handleClose: () => void
  handleConfirm: (changes: any) => void
}

export const EditLocationsDialog = ({
  open,
  article,
  handleClose,
  handleConfirm,
}: OnboardingEditLocationsDialogProps): JSX.Element => {
  const { user } = useUser()
  const { t } = useTranslation()
  const initialArticleLocations: Location[] = _.cloneDeep(article.locations!)
  const [workingArticleLocations, setWorkingArticleLocations] = useState<Location[]>(
    _.cloneDeep(article.locations!)
  )

  const articleComment = article.comment !== ESCAPE_STRING_FOR_EMPTY_COMMENT ? article.comment : ''

  const updateLocationName = (newName: string, index: number) => {
    const copyOfArticleLocations = [...workingArticleLocations]
    copyOfArticleLocations![index].name = newName
    setWorkingArticleLocations(copyOfArticleLocations)
  }

  const handleResetSingleRow = (index: number) => {
    const copyOfArticleLocations = [...workingArticleLocations]
    copyOfArticleLocations[index].name = initialArticleLocations[index].name
    setWorkingArticleLocations(copyOfArticleLocations)
  }

  const handleResetAll = () => {
    setWorkingArticleLocations(initialArticleLocations)
  }

  return (
    <Dialog onClose={handleClose} open={open} fullWidth={true} maxWidth={'md'}>
      <div className="p-4 m-2">
        <div
          className={` ${
            articleComment === '' ? `d-none` : `ms-2 d-flex flex-row align-items-end`
          }`}
        >
          <i className="fa-light fa-message-dots location-panel-comment-icon me-3"></i>

          <TextField
            label="Comment"
            defaultValue={articleComment}
            InputProps={{
              readOnly: true,
            }}
            variant="standard"
            disabled={true}
            className="location-panel-comment"
          />
        </div>
        <div className="d-flex flex-column flex-lg-row align-items-start mt-3">
          <div className="mb-3 mb-lg-0">
            <VisualArticle
              article={article}
              visualArticleSizeClass={'one'}
              displayOnly={true}
              parentEl={LOCATIONS_PANEL}
              currency={user.currency}
            />
          </div>

          <table className="mt-3 w-100">
            <thead>
              <tr>
                <td align="center">
                  <h6>{t('locationPanel.quantityAbbreviation')}</h6>
                </td>

                <td align="center">
                  <h6>{t('common.location')}</h6>
                </td>
                <td align="center">
                  <h6>{t('common.actions')}</h6>
                </td>
              </tr>
            </thead>
            <tbody>
              {workingArticleLocations?.map((location: Location, index: number) => (
                <tr key={index}>
                  <td width={150} align="center">
                    <p>{location.quantity}</p>
                  </td>

                  <td width={350} align="center">
                    <TextField
                      className="locations-panel"
                      data-testid={`location-${index}`}
                      value={location.name ? location.name : 'Default'}
                      onChange={(e) => updateLocationName(e.target.value, index)}
                      style={{ width: '300px', maxWidth: '100%', textAlignLast: 'center' }}
                    />
                  </td>
                  <td width={150} align="center">
                    <Button
                      variant="outlined"
                      data-testid={'reset-' + index}
                      onClick={() => handleResetSingleRow(index)}
                    >
                      Reset
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="d-flex justify-content-end mb-3 mt-5 my-lg-3">
          <div className="mx-3">
            <Button variant="outlined" data-testid="cancel" onClick={handleClose}>
              {t('common.cancel')}
            </Button>
          </div>
          <div className="mx-3">
            <Button
              variant="outlined"
              disabled={
                JSON.stringify(initialArticleLocations) === JSON.stringify(workingArticleLocations)
              }
              data-testid="reset-all"
              onClick={handleResetAll}
            >
              {t('locationPanel.resetAll')}
            </Button>
          </div>

          <div>
            <Button
              data-testid="confirm-changes-location-btn"
              variant="contained"
              disabled={
                JSON.stringify(initialArticleLocations) === JSON.stringify(workingArticleLocations)
              }
              onClick={() => handleConfirm(workingArticleLocations)}
            >
              {t('common.confirm')}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  )
}
